import { Controller } from "@hotwired/stimulus"

// Handles multiple FAQ accordions on the same page with full keyboard support
// Based on toc_collapse_controller pattern but supports multiple independent accordions
export default class extends Controller {
  static targets = ["button", "content", "icon"]

  connect() {
    // Initialize all accordions as collapsed
    this.contentTargets.forEach((content, index) => {
      content.classList.add("hidden")
    })

    // Set initial aria-expanded states
    this.buttonTargets.forEach(button => {
      button.setAttribute("aria-expanded", "false")
    })

    // Bind keydown handler once and store it for cleanup
    this.keydownHandler = this.handleKeydown.bind(this)

    // Add keyboard event listener for arrow navigation
    this.element.addEventListener('keydown', this.keydownHandler)
  }

  disconnect() {
    // Clean up event listener using the same bound function
    this.element.removeEventListener('keydown', this.keydownHandler)
  }

  toggle(event) {
    // Handle both click and keyboard activation (Enter/Space)
    if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') {
      return
    }

    // Prevent default for Space key to avoid page scroll
    if (event.key === ' ') {
      event.preventDefault()
    }

    const button = event.currentTarget
    const index = button.dataset.faqIndex

    // Find the corresponding content and icon by index
    const content = this.contentTargets.find(c => c.dataset.faqIndex === index)
    const icon = this.iconTargets.find(i => i.dataset.faqIndex === index)

    if (!content || !icon) return

    // Check current state
    const isExpanded = !content.classList.contains("hidden")

    if (isExpanded) {
      // Collapse
      content.classList.add("hidden")
      button.setAttribute("aria-expanded", "false")
      icon.classList.remove("rotate-180")
    } else {
      // Expand
      content.classList.remove("hidden")
      button.setAttribute("aria-expanded", "true")
      icon.classList.add("rotate-180")
    }

    // Announce state change for screen readers
    this.announceStateChange(button, !isExpanded)
  }

  // Handle keyboard navigation with arrow keys
  handleKeydown(event) {
    // Only handle arrow keys when focus is on a button
    if (!this.buttonTargets.includes(document.activeElement)) {
      return
    }

    const currentButton = document.activeElement
    const currentIndex = this.buttonTargets.indexOf(currentButton)

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        // Move to next FAQ
        if (currentIndex < this.buttonTargets.length - 1) {
          this.buttonTargets[currentIndex + 1].focus()
        }
        break
      case 'ArrowUp':
        event.preventDefault()
        // Move to previous FAQ
        if (currentIndex > 0) {
          this.buttonTargets[currentIndex - 1].focus()
        }
        break
      case 'Home':
        event.preventDefault()
        // Move to first FAQ
        this.buttonTargets[0].focus()
        break
      case 'End':
        event.preventDefault()
        // Move to last FAQ
        this.buttonTargets[this.buttonTargets.length - 1].focus()
        break
    }
  }

  // Announce state changes for screen reader users
  announceStateChange(button, isExpanded) {
    // Create a temporary aria-live region for announcements
    const announcement = document.createElement('div')
    announcement.setAttribute('aria-live', 'polite')
    announcement.setAttribute('aria-atomic', 'true')
    announcement.className = 'sr-only'

    const questionText = button.textContent.trim()
    announcement.textContent = `${questionText} is now ${isExpanded ? 'expanded' : 'collapsed'}`

    document.body.appendChild(announcement)

    // Remove after announcement
    setTimeout(() => {
      document.body.removeChild(announcement)
    }, 1000)
  }

  // Optional: Expand all FAQs
  expandAll() {
    this.contentTargets.forEach((content, index) => {
      content.classList.remove("hidden")
    })

    this.buttonTargets.forEach(button => {
      button.setAttribute("aria-expanded", "true")
    })

    this.iconTargets.forEach(icon => {
      icon.classList.add("rotate-180")
    })
  }

  // Optional: Collapse all FAQs
  collapseAll() {
    this.contentTargets.forEach((content, index) => {
      content.classList.add("hidden")
    })

    this.buttonTargets.forEach(button => {
      button.setAttribute("aria-expanded", "false")
    })

    this.iconTargets.forEach(icon => {
      icon.classList.remove("rotate-180")
    })
  }
}